import React, { useState, useEffect, useMemo, useRef } from 'react';
import { SkillConfig } from '../../types/onboarding';
import { Image } from '../../../ui/Image';
import { useCreateSkill, useSkill } from '../../apis/skill-registry';
import { Button } from '../../components';
import {
  useGetOutboundIntegrations,
  useLinearTeams,
  useLinearProjects,
} from '../../apis/outbound-integrations';

import { Select } from '@plerionhq/design-system';
import { XCircleIcon } from '@heroicons/react/24/solid';

import { useTranslation, Trans } from 'react-i18next';

interface LinearSkillSetupProps {
  skill: SkillConfig;
  onComplete: () => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export const LinearSkillSetup: React.FC<LinearSkillSetupProps> = ({
  skill,
  onComplete,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useTranslation('pleri/onboarding');
  const [selectedIntegrationId, setSelectedIntegrationId] =
    useState<string>('');
  const [teamId, setTeamId] = useState<string>('');
  const [projectId, setProjectId] = useState<string>('');
  // Tracks whether the saved project has been applied once. Prevents a later
  // config refetch or the preselect effect from overwriting a manual clear.
  const didPreselectProjectRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: enableSkill, isPending: isEnabling } = useCreateSkill();
  const {
    data: integrations,
    isLoading: isLoadingIntegrations,
    refetch: refetchIntegrations,
  } = useGetOutboundIntegrations();

  const handleIntegrationUpdate = (
    _selectedIndex?: number,
    selected?: { name: string; value: string },
  ): void => {
    if (!selected || typeof selected.value !== 'string') {
      setSelectedIntegrationId('');
      return;
    }
    setSelectedIntegrationId(selected.value);
  };

  const handleTeamUpdate = (
    _selectedIndex?: number,
    selected?: { name: string; value: string },
  ): void => {
    if (!selected || typeof selected.value !== 'string') {
      setTeamId('');
      return;
    }
    setTeamId(selected.value);
  };

  const handleProjectUpdate = (
    _selectedIndex?: number,
    selected?: { name: string; value: string },
  ): void => {
    if (!selected || typeof selected.value !== 'string') {
      setProjectId('');
      return;
    }
    setProjectId(selected.value);
  };

  // Fetch existing skill configuration if skill is enabled
  const { data: existingSkill } = useSkill(skill.id, skill.enabled);

  // Get Linear teams and projects for the selected integration
  const { data: teams = [], isLoading: isLoadingTeams } = useLinearTeams(
    selectedIntegrationId,
    !!selectedIntegrationId,
  );
  const { data: projects = [], isLoading: isLoadingProjects } =
    useLinearProjects(selectedIntegrationId, teamId);

  const linearOptions = useMemo(
    () =>
      integrations
        ?.filter((integration) => integration.type === 'Linear')
        .map((integration) => ({
          name: integration.name,
          value: integration.integrationId,
        })) ?? [],
    [integrations],
  );

  // Format teams for the Select component
  const teamOptions = useMemo(() => {
    return (
      teams?.map((team) => ({
        name: team.key ? `${team.key} - ${team.name}` : team.name,
        value: team.id,
      })) ?? []
    );
  }, [teams]);

  // Format projects for the Select component
  const projectOptions = useMemo(() => {
    return (
      projects?.map((project) => ({
        name: project.name,
        value: project.id,
      })) ?? []
    );
  }, [projects]);

  // Auto-select if there's only one integration
  useEffect(() => {
    if (linearOptions.length === 1 && !selectedIntegrationId) {
      setSelectedIntegrationId(linearOptions[0].value);
    }
  }, [linearOptions, selectedIntegrationId]);

  // Reset team and project when integration changes
  useEffect(() => {
    if (selectedIntegrationId !== existingSkill?.config?.integrationId) {
      setTeamId('');
      setProjectId('');
    }
  }, [selectedIntegrationId, existingSkill?.config?.integrationId]);

  // Reset project when team changes
  useEffect(() => {
    if (teamId !== existingSkill?.config?.teamId) {
      setProjectId('');
    }
  }, [teamId, existingSkill?.config?.teamId]);

  // Initialize state with existing configuration
  useEffect(() => {
    if (existingSkill?.config) {
      const config = existingSkill.config;
      if (typeof config.integrationId === 'string') {
        setSelectedIntegrationId(config.integrationId);
      }
      if (typeof config.teamId === 'string') {
        setTeamId(config.teamId);
      }
      // Only seed the saved project before it has been applied once, so a later
      // refetch does not overwrite a project the user has manually cleared.
      if (
        typeof config.projectId === 'string' &&
        !didPreselectProjectRef.current
      ) {
        setProjectId(config.projectId);
      }
    }
  }, [existingSkill?.config]);

  // Preselect the saved project once, after projects load. Runs a single time
  // so a manual clear (or change) is not immediately overwritten.
  useEffect(() => {
    if (didPreselectProjectRef.current) {
      return;
    }
    if (!teamId || projectOptions.length === 0) {
      return;
    }
    const savedProjectId = existingSkill?.config?.projectId;
    if (typeof savedProjectId === 'string' && !projectId) {
      const projectExists = projectOptions.some(
        (option) => option.value === savedProjectId,
      );
      if (projectExists) {
        setProjectId(savedProjectId);
      }
    }
    didPreselectProjectRef.current = true;
  }, [existingSkill?.config?.projectId, teamId, projectOptions, projectId]);

  const handleSubmit = async () => {
    if (isDisabled) {
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      await enableSkill({
        skillType: skill.id,
        enabled: true,
        config: {
          integrationId: selectedIntegrationId,
          teamId: teamId,
          projectId: projectId || undefined,
        },
      });
      await onComplete();
    } catch (error) {
      setError(t('failedToConfigureLinearPleaseTryAgain'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasExistingConfig =
    typeof existingSkill?.config?.integrationId === 'string' ||
    typeof existingSkill?.config?.teamId === 'string' ||
    typeof existingSkill?.config?.projectId === 'string';
  const isReconfiguring = Boolean(
    existingSkill?.enabled || skill.enabled || hasExistingConfig,
  );
  const hasSingleIntegration = linearOptions.length === 1;
  const hasNoIntegrations = linearOptions.length === 0;

  // Poll for integrations when there are none, so we auto-detect additions
  useEffect(() => {
    if (isLoadingIntegrations || !hasNoIntegrations) {
      return;
    }
    const intervalId = window.setInterval(() => {
      void refetchIntegrations();
    }, 4000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [hasNoIntegrations, isLoadingIntegrations, refetchIntegrations]);

  const isDisabled =
    isLoading ||
    isEnabling ||
    isSubmitting ||
    isLoadingIntegrations ||
    isLoadingProjects ||
    hasNoIntegrations ||
    !selectedIntegrationId ||
    !teamId;

  const selectedOption = linearOptions.find(
    (option) => option.value === selectedIntegrationId,
  );

  return (
    <div className="mx-auto max-w-2xl">
      {/* 1. Title */}
      <div className="mb-8 text-center">
        <h2 className="text-2xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
          {t('linear')}
        </h2>
      </div>
      <div className="rounded-lg bg-white p-6 dark:bg-gray-900">
        {/* 2. Logo */}
        <div className="mb-8 text-center">
          <div className="relative mx-auto inline-block">
            <div className="absolute inset-0 animate-[pulse_4s_ease-in-out_infinite] rounded-xl bg-blue-50 blur-xl dark:bg-blue-950/10" />
            {skill.image ? (
              <Image
                src={skill.image.src}
                alt={skill.image.alt}
                className="relative h-20 rounded-xl object-cover"
              />
            ) : (
              <div className="relative flex h-20 w-20 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/20 to-indigo-600/20">
                <span className="text-4xl leading-tight">{skill.icon}</span>
              </div>
            )}
          </div>
        </div>

        {/* 3. Short Description */}
        <div className="mb-8 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            {t('iCanCreateUpdateCommentAndClose')}
          </p>
        </div>

        {/* 4. Learn More Link */}
        <div className="mb-8 text-center">
          <p className="text-sm text-gray-500">
            <a
              href="https://docs.pleri.ai/tools/linear"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-700"
            >
              {t('clickHereToLearnMore')}
            </a>
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-8 rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-950">
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* 5. Form Fields */}
        <div className="mb-8">
          <div className="mx-auto max-w-md">
            {isLoadingIntegrations ? (
              <div className="w-full">
                <div className="flex w-full items-center gap-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600 dark:border-gray-600" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('loadingYourLinearIntegrations')}
                  </p>
                </div>
              </div>
            ) : hasNoIntegrations ? (
              <div className="space-y-4">
                <div className="w-full">
                  <div className="mb-4 rounded-lg bg-blue-50 p-4 dark:bg-blue-950">
                    <div className="flex items-start gap-3">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                        <svg
                          className="h-3 w-3 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-blue-900">
                          {t('addLinearIntegration')}
                        </p>
                        <p className="mt-1 text-sm text-blue-700">
                          <Trans
                            t={t}
                            i18nKey="visitAddLinearIntegrationPageToAdd"
                            components={{
                              a: (
                                <a
                                  href="/settings/integrations/add/Linear/Linear"
                                  className="underline hover:text-blue-800"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={t(
                                    'openAddLinearIntegrationPageInA',
                                  )}
                                />
                              ),
                            }}
                          />
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {hasSingleIntegration ? (
                  <div className="w-full">
                    <div className="flex w-full items-center gap-2 rounded-lg bg-blue-50 p-2 dark:bg-blue-950">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                        <svg
                          className="h-3 w-3 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <p className="text-sm text-blue-800">
                        {selectedOption?.name || t('linearIntegration')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('integration')}
                    </div>
                    <Select
                      key={`linear-integration-${selectedIntegrationId}`}
                      options={linearOptions}
                      defaultSelectedIndex={
                        selectedIntegrationId
                          ? linearOptions.findIndex(
                              (option) =>
                                option.value === selectedIntegrationId,
                            )
                          : -1
                      }
                      placeholder={t('chooseYourLinear')}
                      onUpdate={handleIntegrationUpdate}
                      wrapperClassName="w-full !gap-0 text-sm"
                      hideCloseIcon={true}
                    />
                  </div>
                )}

                {selectedIntegrationId && (
                  <div className="mt-4">
                    <div className="mb-2">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('iWillUseTheSelectedTeamTo')}
                      </p>
                    </div>
                    {isLoadingTeams ? (
                      <div className="flex w-full items-center gap-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600 dark:border-gray-600" />
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t('loadingTeams')}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <div className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('team')}
                        </div>
                        <Select
                          key={`linear-team-${teamId}`}
                          options={teamOptions}
                          defaultSelectedIndex={
                            teamId
                              ? teamOptions.findIndex(
                                  (option) => option.value === teamId,
                                )
                              : -1
                          }
                          placeholder={t('chooseATeam')}
                          onUpdate={handleTeamUpdate}
                          wrapperClassName="w-full !gap-0 text-sm"
                          hideCloseIcon={true}
                        />
                      </div>
                    )}
                  </div>
                )}

                {teamId && (
                  <>
                    {isLoadingProjects ? (
                      <div className="mt-4 flex w-full items-center gap-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600 dark:border-gray-600" />
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t('loadingProjects')}
                        </p>
                      </div>
                    ) : projectOptions.length > 0 ? (
                      <div className="mt-4">
                        <div className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('projectOptional')}
                        </div>
                        <div className="relative">
                          <Select
                            key={`linear-project-${projectId}`}
                            options={projectOptions}
                            defaultSelectedIndex={
                              projectId
                                ? projectOptions.findIndex(
                                    (option) => option.value === projectId,
                                  )
                                : -1
                            }
                            placeholder={t('chooseAProjectOptional')}
                            onUpdate={handleProjectUpdate}
                            wrapperClassName="w-full !gap-0 text-sm"
                            hideCloseIcon={true}
                          />
                          {projectId && (
                            <button
                              type="button"
                              onClick={() =>
                                handleProjectUpdate(undefined, undefined)
                              }
                              aria-label={t('clearSelectedProject')}
                              className="absolute -right-4 bottom-0 flex h-[38px] items-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                            >
                              <XCircleIcon
                                className="h-4 w-4"
                                aria-hidden="true"
                              />
                            </button>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {t('leaveEmptyToCreateIssuesInThe')}
                        </p>
                      </div>
                    ) : (
                      <div className="mt-4">
                        <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-950">
                          <p className="text-sm text-blue-700">
                            {t('noProjectsFoundForThisTeamYou')}
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* 6. Call to Action Button */}
        {!isLoadingIntegrations && !hasNoIntegrations && (
          <div className="mb-6 flex flex-col items-center">
            <Button
              onClick={handleSubmit}
              disabled={isDisabled}
              className={`group relative flex w-full items-center justify-center rounded-lg px-8 py-3 text-sm font-medium transition-all ${
                isDisabled
                  ? 'cursor-not-allowed bg-gray-300 text-gray-500 dark:bg-gray-600 dark:text-gray-400'
                  : ''
              }`}
            >
              {isReconfiguring ? t('updateConfiguration') : t('activateLinear')}
            </Button>
          </div>
        )}

        {/* 7. Cancel or Go Back Button */}
        {!isLoadingIntegrations && !hasNoIntegrations && (
          <div className="flex flex-col items-center">
            <button
              onClick={onCancel}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              {t('common:cancel')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
